import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getAccountSsoConnection, updateAccountSsoConnection} from 'src/openapi/admin/apis/AccountSingleSignOnApi';
import {SsoConnectionRole} from 'src/openapi/admin/models/SsoConnection';
import {useAppDispatch} from 'src/store/hooks';

import type {SsoConnection} from 'src/openapi/admin/models/SsoConnection';

type Props = {
    accountId: number,
};

export const useSsoSettings = ({accountId}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const loadConnectionResponse = await apiDispatch(getAccountSsoConnection(
            {accountId},
            {signal},
        ));

        if (loadConnectionResponse.payload.success && !loadConnectionResponse.payload.data.domains?.length) {
            const payload: {success: true, error: false, data: SsoConnection} = {
                success: true,
                error: false,
                data: {
                    ...loadConnectionResponse.payload.data,
                    domains: [''],
                },
            };

            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: loadConnectionResponse.payload});
        }
    }, [accountId, apiDispatch]);

    const saveDispatchAction = useCallback((ssoConnection: SsoConnection, signal: AbortSignal) => apiDispatch(
        updateAccountSsoConnection({
            accountId,
            ssoConnection: {
                ...ssoConnection,
                domains: ssoConnection.domains.map(domain => domain.trim()).filter(Boolean),
            },
        }, {signal}),
    ), [accountId, apiDispatch]);

    return useCommonDataHook({
        getDispatchAction,
        saveDispatchAction,
        initValue: {
            ssoEnabled: false,
            ssoMandatory: false,
            ssoUrl: '',
            ssoCertificate: '',
            domains: [],
            jitEnabled: false,
            role: SsoConnectionRole.STANDALONE,
            canUpdateSsoEnabled: false,
        },
    });
};
